import React, { useCallback, useMemo, useState } from 'react';
import { CompositeDataSetResumeParserRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetResumeParserRecipeResumeGradingConfig } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { z } from 'zod';
import { DataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { DataSetCompositeRecipeDataWizardTrackerItem } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { useDataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { DataSetCompositeCompleteIndicator } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeCompleteIndicator';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { TaggedDataSetReference } from 'libs/end-to-end/src/types/databases/data-sets/data-set-reference';
import { WorkflowDataSetSelector } from 'apps/sigma2-ui/src/components/workflows/WorkflowDataSetSelector';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { DataSets } from 'libs/end-to-end/src/types/databases/data-sets/data-sets';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { SimpleInput } from 'apps/sigma2-ui/src/components/ui/SimpleInput';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { AllowedOpenAiModels } from 'libs/end-to-end/src/types/databases/data-sets/data-set-resume-rubric';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { SelectiveFilterStringBasedOptionComponent } from 'apps/sigma2-ui/src/SelectiveFilteringOptionComponent';
import { Modal } from 'apps/sigma2-ui/src/components/ui/Modal';
import { useRubricConstructsData } from 'apps/sigma2-ui/src/data-wizard/components/use-rubric-constructs-data';
import { DatabaseRubricBankConstructsView } from 'apps/sigma2-ui/src/data-wizard/components/DatabaseRubricBankConstructsView';
import { useCrudOperations } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';
import { ReactNode } from 'react';

const ResumeParserConfigStep = z.enum([
  'parsing-config',
  'grading-config',
  'complete',
]);

type ResumeParserConfigStep = z.infer<typeof ResumeParserConfigStep>;

export const DataSetCompositeRecipeResumeParserConfigComponent = ({
  organizationId,
  databaseId,
  config,
  setConfig,
  taggedDataSetReferences,
}: {
  organizationId: string;
  databaseId: string;
  config: CompositeDataSetResumeParserRecipe;
  setConfig: (config: CompositeDataSetRecipe) => void;
  taggedDataSetReferences: TaggedDataSetReference[];
}) => {
  const patternListDataSetsGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets`;
  }, [organizationId, databaseId]);

  const listDataSetsPattern = useSimpleReadPattern({
    responseSchema: DataSets,
    canRead: true,
    getUrlFn: patternListDataSetsGetUrlFn,
  });
  const stepItems: DataSetCompositeRecipeDataWizardTrackerItem<ResumeParserConfigStep>[] =
    useMemo(() => {
      return [
        { value: 'parsing-config', display: 'Parsing Configuration' },
        { value: 'grading-config', display: 'Grading Configuration' },
        { value: 'complete', display: 'Complete' },
      ];
    }, []);

  const dataWizardPayload =
    useDataSetCompositeRecipeDataWizardTracker<ResumeParserConfigStep>({
      items: stepItems,
      onNext: () => true,
    });

  const rubricConfigOps = useCrudOperations({
    items: config.gradingConfig?.rubricsByJobTitle ?? [],
    setItems: (configs) => {
      if (config.gradingConfig) {
        setConfig({
          ...config,
          gradingConfig: {
            ...config.gradingConfig,
            rubricsByJobTitle: configs,
          },
        });
      }
    },
  });

  const [showConstructsModal, setShowConstructsModal] = useState(false);
  const [currentRubricConfigIndex, setCurrentRubricConfigIndex] = useState<
    number | null
  >(null);

  const useRubricConstructsDataPayload = useRubricConstructsData({
    organizationId,
    databaseId,
    isSelectEnabled: true,
  });

  const currentUiToShow = useMemo(() => {
    if (dataWizardPayload.selectedItem === null) {
      return null;
    }

    switch (dataWizardPayload.selectedItem) {
      case 'parsing-config':
        return (
          <Spacing size={'md'} direction={'vertical'}>
            <WorkflowDataSetSelector
              title={'Source Dataset'}
              dataSetId={config.dataSetId}
              setDataSetId={(id: string) => {
                setConfig({
                  ...config,
                  dataSetId: id ? id : '',
                });
              }}
              filterFn={(dataSet) => {
                return taggedDataSetReferences.some(
                  (taggedDataSetReference) =>
                    taggedDataSetReference.dataSetReference.dataSetId ===
                    dataSet.id,
                );
              }}
              listDataSetsPattern={listDataSetsPattern}
            />

            <SimpleInput
              label="Input File Name Prefix"
              value={config.fileName}
              onChange={(value) => {
                setConfig({
                  ...config,
                  fileName: value,
                });
              }}
              placeholder="e.g., parsed_resumes.csv"
            />

            <S2Checkbox
              checked={config.useV2Grading}
              onChange={() => {
                setConfig({
                  ...config,
                  useV2Grading: !config.useV2Grading,
                });
              }}
            >
              Use Resume Parser V2
            </S2Checkbox>

            <S2Checkbox
              checked={config.disableDbtJob ?? false}
              onChange={() => {
                setConfig({
                  ...config,
                  disableDbtJob: !(config.disableDbtJob ?? false),
                });
              }}
            >
              Disable DBT Job
            </S2Checkbox>

            <S2Checkbox
              checked={config.writeOutputToS3 ?? false}
              onChange={() => {
                setConfig({
                  ...config,
                  writeOutputToS3: !(config.writeOutputToS3 ?? false),
                });
              }}
            >
              Write Parsed Resumes to S3
            </S2Checkbox>

            <SimpleInput
              label={'Resume Parsing Cache Buster'}
              value={config.resumeParsingCacheBusterVersion}
              onChange={(value: string) => {
                if (value !== null) {
                  setConfig({
                    ...config,
                    resumeParsingCacheBusterVersion: value,
                  });
                }
              }}
            />
          </Spacing>
        );
      case 'grading-config':
        return (
          <Spacing size={'md'} direction={'vertical'}>
            <S2Checkbox
              checked={config.gradingConfig !== null}
              onChange={() => {
                if (config.gradingConfig) {
                  setConfig({
                    ...config,
                    gradingConfig: null,
                  });
                } else {
                  setConfig({
                    ...config,
                    gradingConfig: {
                      rubricsByJobTitle: [],
                      openAiModel: 'gpt-4o-mini',
                      enableHireDateFiltering: false,
                    },
                  });
                }
              }}
            >
              Enable Resume Grading
            </S2Checkbox>
            {config.gradingConfig && (
              <Spacing size={'lg'} direction={'vertical'}>
                <SelectBoxWithSearch
                  type={'single'}
                  setSelected={(value) => {
                    if (config.gradingConfig) {
                      setConfig({
                        ...config,
                        gradingConfig: {
                          ...config.gradingConfig,
                          openAiModel: value as AllowedOpenAiModels,
                        },
                      });
                    }
                  }}
                  selected={config.gradingConfig.openAiModel}
                  list={AllowedOpenAiModels.options.map((model) => {
                    return {
                      optionLabel: model,
                      optionValue: model,
                    };
                  })}
                  label={'OpenAI Model'}
                />

                <S2Checkbox
                  checked={
                    config.gradingConfig.enableHireDateFiltering ?? false
                  }
                  onChange={() => {
                    if (config.gradingConfig) {
                      setConfig({
                        ...config,
                        gradingConfig: {
                          ...config.gradingConfig,
                          enableHireDateFiltering: !(
                            config.gradingConfig.enableHireDateFiltering ??
                            false
                          ),
                        },
                      });
                    }
                  }}
                >
                  Enable Resume Grading Filtering
                </S2Checkbox>

                <Button
                  behavior={{
                    type: 'onClick',
                    onClick: () => {
                      rubricConfigOps.onAdd({
                        filter: {
                          type: 'equals',
                          value: '',
                        },
                        rubricConstructId: '',
                      });
                    },
                  }}
                  size={'small'}
                  variant={'primary'}
                  text={'Create'}
                />

                <Table
                  items={config.gradingConfig.rubricsByJobTitle ?? []}
                  columnDefinitions={[
                    {
                      id: 'filter',
                      header: () => 'Filter',
                      cell: ({ item, rowIndex }) => (
                        <SelectiveFilterStringBasedOptionComponent
                          option={item.filter}
                          setOption={(option) => {
                            rubricConfigOps.onEdit(rowIndex, {
                              filter: option,
                            });
                          }}
                        />
                      ),
                    },
                    {
                      id: 'rubric_item',
                      header: () => 'Rubric Item',
                      cell: ({ item, rowIndex }) => {
                        const foundItem =
                          useRubricConstructsDataPayload.constructs.find(
                            (i) => i.id === item.rubricConstructId,
                          );

                        let itemIdItem: ReactNode;

                        if (item.rubricConstructId === '' || !foundItem) {
                          itemIdItem = <div>{'N/A'}</div>;
                        } else {
                          itemIdItem = <div>{foundItem.name}</div>;
                        }

                        return (
                          <Spacing size={'md'} direction={'vertical'}>
                            {itemIdItem}
                            <Button
                              behavior={{
                                type: 'onClick',
                                onClick: () => {
                                  useRubricConstructsDataPayload.singleSelectConfig?.setCurrentConstructId(
                                    item.rubricConstructId,
                                  );
                                  setCurrentRubricConfigIndex(rowIndex);
                                  setShowConstructsModal(true);
                                },
                              }}
                              size={'small'}
                              variant={'outline'}
                              text={'Select Item'}
                            />
                          </Spacing>
                        );
                      },
                    },
                    {
                      id: 'delete',
                      header: () => <></>,
                      cell: ({ rowIndex }) => {
                        return (
                          <Button
                            behavior={{
                              type: 'onClick',
                              onClick: () => {
                                rubricConfigOps.onDelete(rowIndex);
                              },
                            }}
                            variant={'destructive'}
                            size={'small'}
                            text={'Delete'}
                          />
                        );
                      },
                    },
                  ]}
                />
              </Spacing>
            )}
          </Spacing>
        );
      case 'complete':
        return <DataSetCompositeCompleteIndicator />;
    }
  }, [
    config,
    dataWizardPayload.selectedItem,
    listDataSetsPattern,
    setConfig,
    taggedDataSetReferences,
    useRubricConstructsDataPayload,
    rubricConfigOps,
    setCurrentRubricConfigIndex,
    setShowConstructsModal,
  ]);

  return (
    <Spacing size={'lg'} direction={'vertical'}>
      <Modal
        show={showConstructsModal}
        onClose={() => {
          setShowConstructsModal(false);
          setCurrentRubricConfigIndex(null);
        }}
        title={'Choose Rubric Items'}
        maxWidthOverride="max-w-screen-md"
        scrollable={true}
      >
        <DatabaseRubricBankConstructsView
          payload={useRubricConstructsDataPayload}
          isEditable={false}
        />

        <Button
          behavior={{
            type: 'onClick',
            onClick: () => {
              if (
                currentRubricConfigIndex !== null &&
                useRubricConstructsDataPayload.singleSelectConfig
                  ?.currentConstructId
              ) {
                rubricConfigOps.onEdit(currentRubricConfigIndex, {
                  rubricConstructId:
                    useRubricConstructsDataPayload.singleSelectConfig
                      .currentConstructId,
                });
              }

              setShowConstructsModal(false);
              setCurrentRubricConfigIndex(null);
            },
          }}
          variant={'primary'}
          size={'small'}
          text={'Save'}
        />
      </Modal>
      <DataSetCompositeRecipeDataWizardTracker payload={dataWizardPayload} />
      {currentUiToShow}
      {dataWizardPayload.paginationButtons}
    </Spacing>
  );
};
